import { useCallback, useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from './LoadingSpinner';
import { MfaChallengeForm } from './MfaChallengeForm';
import { MfaEnrollForm } from './MfaEnrollForm';
import {
  getMfaTrustVersion,
  hasValidMfaDeviceTrust,
  isMfaRequired,
} from '@/utils/mfaDeviceTrust';

type MfaGateProps = {
  user: User;
  children: React.ReactNode;
};

type GateMode = 'loading' | 'pass' | 'challenge' | 'enroll';

const MFA_EXIT_MS = 500;

export function MfaGate({ user, children }: MfaGateProps) {
  const [mode, setMode] = useState<GateMode>('loading');
  const [leaving, setLeaving] = useState(false);

  const evaluate = useCallback(async () => {
    setMode('loading');
    try {
      // Prefer fresh user so newly toggled app_metadata.mfa_required is visible.
      const { data: freshData } = await supabase.auth.getUser();
      const effectiveUser = freshData.user || user;
      const required = isMfaRequired(effectiveUser);
      const trustVersion = getMfaTrustVersion(effectiveUser);

      const { data: aal, error: aalError } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalError) throw aalError;

      const needsChallenge =
        aal.nextLevel === 'aal2' && aal.currentLevel !== 'aal2';

      if (needsChallenge) {
        if (hasValidMfaDeviceTrust(effectiveUser.id, trustVersion)) {
          setMode('pass');
          return;
        }
        setMode('challenge');
        return;
      }

      if (required && aal.nextLevel === 'aal1') {
        // Required but no verified factor yet
        setMode('enroll');
        return;
      }

      setMode('pass');
    } catch (e) {
      console.error('MFA gate evaluation failed', e);
      // Fail closed for required users; otherwise allow through
      if (isMfaRequired(user)) setMode('challenge');
      else setMode('pass');
    }
  }, [user]);

  useEffect(() => {
    void evaluate();
  }, [evaluate]);

  const passWithExit = () => {
    setLeaving(true);
    window.setTimeout(() => {
      setMode('pass');
      setLeaving(false);
    }, MFA_EXIT_MS);
  };

  if (mode === 'loading') return <LoadingSpinner />;
  if (mode === 'challenge') {
    return (
      <MfaChallengeForm
        user={user}
        onVerified={passWithExit}
        leaving={leaving}
      />
    );
  }
  if (mode === 'enroll') {
    return (
      <MfaEnrollForm
        user={user}
        onEnrolled={passWithExit}
        leaving={leaving}
      />
    );
  }
  return <>{children}</>;
}
